import { nowUtc } from '@/lib/clock';

/**
 * 三層存檔的第一層：write-ahead 草稿（desktop-app.md 10.6）。
 *
 * 每次按鍵都同步寫進 localStorage。用 localStorage 而不是 IndexedDB，
 * 是因為它是**同步 API**：分頁被關掉的那一瞬間不會有「還沒 await 完」的狀態，
 * 微秒級的成本也不值得為它做 debounce。
 *
 * 正本仍然是 Dexie，草稿只在「正本比草稿舊」時派上用場。
 */

export const NOTE_DRAFT_PREFIX = 'note.draft.';

export interface NoteDraft {
  contentJson: string;
  /** 寫下這份草稿的 UTC 時間，用來跟正本的 updatedAt 比較。 */
  savedAt: string;
}

export function writeNoteDraft(noteId: string, contentJson: string, at: string = nowUtc()): void {
  try {
    const draft: NoteDraft = { contentJson, savedAt: at };
    localStorage.setItem(`${NOTE_DRAFT_PREFIX}${noteId}`, JSON.stringify(draft));
  } catch {
    // 空間滿了或隱私模式禁用 localStorage 都不該讓打字中斷。
    // 正本仍然會在 400ms 後寫進 Dexie，只是少了關窗那一瞬間的保護。
  }
}

export function readNoteDraft(noteId: string): NoteDraft | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(`${NOTE_DRAFT_PREFIX}${noteId}`);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const draft = parsed as Partial<NoteDraft>;
    if (typeof draft.contentJson !== 'string' || typeof draft.savedAt !== 'string') return null;
    return { contentJson: draft.contentJson, savedAt: draft.savedAt };
  } catch {
    return null;
  }
}

export function clearNoteDraft(noteId: string): void {
  try {
    localStorage.removeItem(`${NOTE_DRAFT_PREFIX}${noteId}`);
  } catch {
    // 讀不到 localStorage 時沒有草稿可清。
  }
}

export function listDraftNoteIds(): string[] {
  const ids: string[] = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(NOTE_DRAFT_PREFIX)) ids.push(key.slice(NOTE_DRAFT_PREFIX.length));
    }
  } catch {
    return [];
  }
  return ids;
}

/** 筆記被永久刪除後，它的草稿也要跟著走，否則會一直佔著 localStorage。 */
export function pruneNoteDrafts(keepIds: ReadonlySet<string>): void {
  for (const id of listDraftNoteIds()) {
    if (!keepIds.has(id)) clearNoteDraft(id);
  }
}

export interface ResolvedNoteContent {
  contentJson: string;
  /** 真的從草稿救回了東西；狀態列可以據此顯示一次提示。 */
  fromDraft: boolean;
}

/**
 * 開檔時決定要載入哪一份內容。
 *
 * 草稿比正本新就**靜默採用草稿**，不彈窗詢問（10.6）：
 * 使用者不會知道「草稿」與「正本」的差別，問了只會困惑。
 */
export function resolveNoteContent(note: {
  id: string;
  contentJson: string;
  updatedAt: string;
}): ResolvedNoteContent {
  const draft = readNoteDraft(note.id);
  if (!draft) return { contentJson: note.contentJson, fromDraft: false };
  if (draft.savedAt <= note.updatedAt) return { contentJson: note.contentJson, fromDraft: false };
  if (draft.contentJson === note.contentJson) {
    return { contentJson: note.contentJson, fromDraft: false };
  }
  return { contentJson: draft.contentJson, fromDraft: true };
}
